'use client';

import appShellStore from '@/lib/offline/appShellStore';
import { getApiUrl } from '@/lib/apiConfig';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

export interface PendingBomItemPayload {
  item_name: string;
  item_code?: string;
  item_id?: string;
  quantity: number;
  unit: string;
  unit_cost?: number;
  line_type?: string;
}

export interface PendingBomItem {
  id: string;
  workItemId: string;
  payload: PendingBomItemPayload;
  createdAt: string;
  retryCount: number;
  lastError?: string;
}

const BOM_ITEM_KEY = 'smarttask_pending_bom_items';
const MAX_BOM_ITEM_RETRIES = 5;

export async function loadPendingBomItems(clientId: number): Promise<PendingBomItem[]> {
  return appShellStore.loadSettingsArrayMigration<PendingBomItem>(clientId, BOM_ITEM_KEY);
}

export async function savePendingBomItems(clientId: number, entries: PendingBomItem[]): Promise<void> {
  await appShellStore.saveSettingsArrayMigration(clientId, BOM_ITEM_KEY, entries);
}

export async function getPendingBomItemCount(clientId: number): Promise<number> {
  if (!clientId) return 0;
  const entries = await loadPendingBomItems(clientId);
  return entries.length;
}

export async function syncPendingBomItems(params: {
  clientId: number;
  token: string;
  onProgress?: (progress: { current: number; total: number; message: string }) => void;
}): Promise<{ synced: number; failed: number; remaining: number }> {
  const { clientId, token, onProgress } = params;
  if (!clientId || !token) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const queue = await loadPendingBomItems(clientId);
  if (queue.length === 0) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const remaining: PendingBomItem[] = [];
  let synced = 0;
  let failed = 0;

  for (let index = 0; index < queue.length; index += 1) {
    const entry = queue[index];
    onProgress?.({
      current: index + 1,
      total: queue.length,
      message: `Syncing BOM item ${index + 1} of ${queue.length}`,
    });

    try {
      const res = await fetch(getApiUrl(`/api/v1/work-items/${entry.workItemId}/bom`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry.payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message = body?.error || 'Failed to sync BOM item';
        if (res.status >= 400 && res.status < 500) {
          throw new Error(`NON_RETRYABLE:${message}`);
        }
        throw new Error(message);
      }

      synced += 1;
    } catch (error: unknown) {
      failed += 1;
      const errMsg = error instanceof Error ? error.message : String(error);
      const nonRetryable = typeof errMsg === 'string' && errMsg.startsWith('NON_RETRYABLE:');
      const nextRetryCount = entry.retryCount + 1;
      if (!nonRetryable && nextRetryCount < MAX_BOM_ITEM_RETRIES) {
        remaining.push({
          ...entry,
          retryCount: nextRetryCount,
          lastError: errMsg || 'Sync failed',
        });
      } else {
        if (!isSilentFetchError(error)) {
          console.error('[SmartTaskOfflineBom] Dropping failed BOM item:', errMsg || error);
        }
      }
    }
  }

  await savePendingBomItems(clientId, remaining);
  return { synced, failed, remaining: remaining.length };
}
